import {
  selectUserLinks,
  selectUserRefs,
  selectUserTerms,
} from "Redux/UserSlice";
import { selectAreRefsVisible, selectStyle } from "Redux/appSlice";
import { useAppSelector } from "Redux/hooks";
import { useRef } from "react";
import WebLink from "../../Components/Atoms/LinkWithBackground";
import { shadeColor } from "../../Logic/ShadeColor";
import ReferenceLink from "./ReferenceLink";

type Props = {
  text: any;
  size?: string;
  justText?: boolean;
  setReferences?: (id: any) => void;
  className?: string;
  textColor?: string;
  setTerms?: (id: any) => void;
};

/*
  @function RichTextReader
  render rich text and render a react component
  @param text {array} - text to render
  @param size {string} - select text size
  @param justText {boolean} - if content should be rendered as just text
  @returns {React.Node}
*/
function RichTextReader(props: Props) {
  const {
    text,
    size = "1em",
    justText = false,
    className = "",
    textColor,
    setTerms,
  } = props;
  const { color2, color3, color4 } = useAppSelector(selectStyle);
  const userTerms = useAppSelector(selectUserTerms);
  const userLinks = useAppSelector(selectUserLinks);
  const userRefs = useAppSelector(selectUserRefs);
  const refsAreVisible = useAppSelector(selectAreRefsVisible);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const shadedColor3 = shadeColor(color4, 15);

  if (text[0] === "p") {
    return (
      <p
        style={{ fontSize: size, color: textColor ? textColor : color4 }}
        className={`my-4 font-semibold ${className}`}
      >
        <RichTextReader
          text={text.slice(2)}
          justText={justText}
          textColor={textColor}
          setTerms={setTerms}
        />
      </p>
    );
  }

  if (text[0] === "em") {
    return (
      <em>
        <RichTextReader
          text={text.slice(2)}
          justText={justText}
          textColor={textColor}
          setTerms={setTerms}
        />
      </em>
    );
  }

  if (text[0] === "strong") {
    return (
      <strong>
        <RichTextReader
          text={text.slice(2)}
          justText={justText}
          textColor={textColor}
          setTerms={setTerms}
        />
      </strong>
    );
  }

  if (text[0] === "term") {
    if (justText) {
      return (
        <RichTextReader
          text={text.slice(2)}
          justText={justText}
          textColor={textColor}
          setTerms={setTerms}
        />
      );
    }

    if (text[1] && text[1]["data-term-id"]) {
      if (userTerms && userTerms[text[1]["data-term-id"]]) {
        return (
          <a
            className="cursor-pointer underline decoration-dotted decoration-1 underline-offset-[6px]"
            style={{ color: color2 }}
            onClick={(e) => {
              e.preventDefault();
              if (setTerms) {
                setTerms(text[1]["data-term-id"]);
              }
            }}
            ref={anchorRef}
            onMouseOver={() => {
              if (anchorRef.current) {
                anchorRef.current.style.color = shadedColor3;
              }
            }}
            onMouseOut={() => {
              if (anchorRef.current) {
                anchorRef.current.style.color = color2;
              }
            }}
          >
            <RichTextReader
              text={text.slice(2)}
              justText={justText}
              textColor={textColor}
              setTerms={setTerms}
            />
          </a>
        );
      } else {
        return (
          <>
            {isTesting() && <a className="text-red-500">(term in db) </a>}
            <RichTextReader
              text={text.slice(2)}
              justText={justText}
              textColor={textColor}
              setTerms={setTerms}
            />
          </>
        );
      }
    }
    return (
      <>
        {isTesting() && <a className="text-red-500">(term in text) </a>}
        <RichTextReader
          text={text.slice(2)}
          justText={justText}
          textColor={textColor}
          setTerms={setTerms}
        />
      </>
    );
  }

  if (text[0] === "web-link") {
    if (justText) {
      return (
        <RichTextReader
          text={text.slice(2)}
          justText={justText}
          textColor={textColor}
          setTerms={setTerms}
        />
      );
    }

    if (text[1] && text[1]["data-web-link-id"]) {
      if (userLinks && userLinks[text[1]["data-web-link-id"]]) {
        return (
          <WebLink
            label={
              <RichTextReader
                text={text.slice(2)}
                justText={justText}
                textColor={textColor}
                setTerms={setTerms}
              />
            }
            href={`${userLinks[text[1]["data-web-link-id"]].address}`}
          ></WebLink>
        );
      } else {
        return (
          <>
            {isTesting() && <a className="text-red-500">(link in db) </a>}
            <RichTextReader
              text={text.slice(2)}
              justText={justText}
              textColor={textColor}
              setTerms={setTerms}
            />
          </>
        );
      }
    }
    return (
      <>
        {isTesting() && <a className="text-red-500">(link in text) </a>}
        <RichTextReader
          text={text.slice(2)}
          justText={justText}
          textColor={textColor}
          setTerms={setTerms}
        />
      </>
    );
  }

  if (
    text[0] === "ref" &&
    userRefs &&
    text[1] &&
    "data-ref-id" in text[1] &&
    userRefs[text[1]["data-ref-id"]]
  ) {
    if (!refsAreVisible) return null;
    const ref = userRefs[text[1]["data-ref-id"]];
    return <ReferenceLink label={ref.code} href={ref.link} />;
  }

  if (text[0] === "br") {
    return <br />;
  }

  // Wrap refs into div
  if (Array.isArray(text) && Array.isArray(text[0]) && text[0][0] === "ref") {
    return (
      <div className="flex flex-row flex-wrap">
        {text.map((el: any, i: any) => {
          if (Array.isArray(el)) {
            return (
              <RichTextReader
                key={i}
                text={el}
                size={size}
                justText={justText}
                textColor={textColor}
                setTerms={setTerms}
              />
            );
          }
          return el;
        })}
      </div>
    );
  }

  return (
    <>
      {text.map((el: any, i: any) => {
        if (Array.isArray(el)) {
          return (
            <RichTextReader
              key={i}
              text={el}
              size={size}
              justText={justText}
              textColor={textColor}
              setTerms={setTerms}
            />
          );
        }
        return el;
      })}
    </>
  );
}

function isTesting(): boolean {
  return (
    process.env.REACT_APP_ENDPOINT === "https://backend-testing.leanentries.com"
  );
}

export default RichTextReader;
